import { useEffect, useMemo, useRef, useState } from 'react';
import { IoChevronDown, IoSearch } from 'react-icons/io5';

import { PHONE_COUNTRIES, normalizePhoneSearch } from '../../utils/phoneCountries';
import styles from './countryphoneselect.module.css';

function CountryPhoneSelect({
  value,
  onChange,
  countries = PHONE_COUNTRIES,
  label = 'País',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selectedCountry = value || countries[0];

  const filteredCountries = useMemo(() => {
    const normalizedQuery = normalizePhoneSearch(query);
    if (!normalizedQuery) return countries;
    const queryDigits = normalizedQuery.replace(/\D/g, '');

    return countries.filter((country) => {
      const searchText = country.searchText || normalizePhoneSearch(`${country.iso} ${country.name} ${country.ddi}`);
      const matchesNameOrIso = searchText.includes(normalizedQuery);
      const matchesDdi = queryDigits ? country.ddi.includes(queryDigits) : false;

      return matchesNameOrIso || matchesDdi;
    });
  }, [countries, query]);

  useEffect(() => {
    if (!open) return undefined;

    searchRef.current?.focus();

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSelect = (country) => {
    onChange?.(country);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={styles.wrapper} ref={rootRef}>
      <label className={styles.label} htmlFor="country-phone-search">
        {label}
      </label>

      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={styles.flag} aria-hidden="true">{selectedCountry.flag}</span>
        <span className={styles.ddi}>+{selectedCountry.ddi}</span>
        <IoChevronDown className={styles.chevron} size={16} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchBox}>
            <IoSearch size={16} aria-hidden="true" />
            <input
              id="country-phone-search"
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar país ou DDI"
              className={styles.searchInput}
              autoComplete="off"
            />
          </div>

          <div className={styles.options} role="listbox">
            {filteredCountries.map((country) => (
              <button
                key={country.iso}
                type="button"
                className={`${styles.option} ${country.iso === selectedCountry.iso ? styles.optionSelected : ''}`}
                onClick={() => handleSelect(country)}
                role="option"
                aria-selected={country.iso === selectedCountry.iso}
              >
                <span className={styles.optionFlag} aria-hidden="true">{country.flag}</span>
                <span className={styles.optionName}>{country.name}</span>
                <span className={styles.optionDdi}>+{country.ddi}</span>
              </button>
            ))}

            {filteredCountries.length === 0 && (
              <div className={styles.empty}>Nenhum país encontrado.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CountryPhoneSelect;
